import { useCallback } from 'react';
import { Account } from 'shared/types/account';
import { ClaimInsuranceBody } from 'shared/types/account/Account';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { mutate } from 'swr';
import {
  OrderDetailsResponse,
  OrderHistoryResponse,
  PreferencesResponse,
} from '@/components/customercenter-ui/components/customer-detail/types';
import { useToast } from '@/components/customercenter-ui/components/ui/use-toast';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { ErrorNotification } from '@/helpers/utils/notification';
import {
  CustomerNotesPayload,
  CustomerPreferencePayload,
  AddressPayload,
  OrderHistoryPayload,
  AddNotePayload,
  UpdateCustomerDetialsPayload,
  RefundHistoryPayload,
  CatalogMailingHistoryPayload,
  updateCustomerPreferencesPayload,
  SendCatalogPayload,
  UpdateEmailPayload,
  UpdatePhonePayload,
  AddDivisionPayload,
  OrderByIdPayload,
  TokenizeCardPayload,
  EnrollProtectionPayload,
  OrderTrackLogPayload,
  SendTracerPayload,
  CreateCustomerPayload,
  CreditHistory,
  CustomerAddressPayload,
  CustomerDetailsPayload,
  ForgotPassword,
  UpdateOrderPayload,
  PhoneRecordingPayload,
  ValidateAddress,
  HoldOrder,
  CTAccount,
  SelectedSitePayload,
  OrderDetailsPayload,
} from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { sdk } from 'sdk';
import { UpdateAccount, UseAccountReturn } from './types';
import { RESPONSE_BAD_REQUEST } from '../../../constants/constant';

const useAccount = (): UseAccountReturn => {
  const projectSettings: ProjectSettings = useProjSettings();
  const { toast } = useToast();

  const requestPasswordReset = useCallback(async (email: string): Promise<void> => {
    const extensions = sdk.composableCommerce;

    const payload = {
      email,
    };

    await extensions.account.requestResetPassword(payload);
  }, []);

  const update = useCallback(async (account: UpdateAccount): Promise<Account> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateAccount(account);

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const getCustomerNotes = useCallback(async (payload: CustomerNotesPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.getCustomerNotes(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const getCustomerPreferences = useCallback(
    async (payload: CustomerPreferencePayload): Promise<PreferencesResponse> => {
      const extensions = sdk.composableCommerce;

      const res = await extensions.account.getCustomerPrefrence(payload);

      return res.isError ? ({} as PreferencesResponse) : res.data;
    },
    [],
  );

  const getOrderHistory = useCallback(async (payload: OrderHistoryPayload): Promise<OrderHistoryResponse> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.getOrderHistory(payload);

    return res.isError ? ({} as OrderHistoryResponse) : res.data;
  }, []);

  const getOrderDetails = useCallback(async (payload: OrderDetailsPayload): Promise<OrderDetailsResponse> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.getOrderDetails(payload);

    return res.isError ? ({} as OrderDetailsResponse) : res.data;
  }, []);

  const addNewAddress = useCallback(async (payload: AddressPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.addNewAddress(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const updateCustomerAddress = useCallback(async (payload: AddressPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateCustomerAddress(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const addNote = useCallback(async (payload: AddNotePayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.addNote(payload);

    mutate('/action/soaCustomer/customer-details');

    return res.isError ? ({} as any) : res.data;
  }, []);

  const updateCustomerDetails = useCallback(async (payload: UpdateCustomerDetialsPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateCustomerDetails(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const getCustomerRefundCheckHistory = useCallback(async (payload: RefundHistoryPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.getCustomerRefundHistory(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const getCustomerMailingHistory = useCallback(async (payload: CatalogMailingHistoryPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.getCustomerMailingHistory(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const makePayment = useCallback(async (payload: any): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res: any = await extensions.account.makePayment(payload);

    const submitPaymentResults = res?.data?.SubmitPaymentResults ?? {};

    if (submitPaymentResults && submitPaymentResults?.bankCode) {
      const bankCode = submitPaymentResults?.bankCode;
      const paymentErrorMapping = JSON.parse(projectSettings?.paymentErrors ?? '');

      if (Object.keys(paymentErrorMapping).includes(bankCode)) {
        const errorMessage = paymentErrorMapping[bankCode];

        ErrorNotification(toast, {
          description: errorMessage,
          action: <ErrorIcon className="h-5 w-5" />,
        });

        return {
          code: 'FAILED',
          message: 'FAILED',
        };
      }
    }

    return res.isError ? ({} as any) : res.data;
  }, []);

  const updateCustomerPreferences = useCallback(async (payload: updateCustomerPreferencesPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateCustomerPreferences(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);
  const setSelectedSiteInSession = useCallback(async (payload: SelectedSitePayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.selectedSiteInSession(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const sendCatalog = useCallback(async (payload: SendCatalogPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.sendCatalog(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const updateEmail = useCallback(async (payload: UpdateEmailPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updateEmail(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const updatePhone = useCallback(async (payload: UpdatePhonePayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.updatePhone(payload);
    return res.isError ? ({} as any) : res.data;
  }, []);

  const addDivision = useCallback(async (payload: AddDivisionPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.addDivision(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const getOrderById = useCallback(async (payload: OrderByIdPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.getOrderById(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const tokenizeCard = useCallback(async (payload: TokenizeCardPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res: any = await extensions.account.tokenizeCard(payload);

    const tokenizeCardResults = res?.data?.TokenizeCardResults ?? {};

    if (tokenizeCardResults && tokenizeCardResults?.bankCode) {
      const bankCode = tokenizeCardResults?.bankCode;
      const paymentErrorMapping = JSON.parse(projectSettings?.paymentErrors ?? '');

      if (Object.keys(paymentErrorMapping).includes(bankCode)) {
        const errorMessage = paymentErrorMapping[bankCode];

        ErrorNotification(toast, {
          description: errorMessage,
          action: <ErrorIcon className="h-5 w-5" />,
        });

        return {
          code: 'FAILED',
          message: 'FAILED',
        };
      }
    }

    return res.isError ? ({} as any) : res.data;
  }, []);

  const enrollCreditProtection = useCallback(async (payload: EnrollProtectionPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.enrollCreditProtection(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const getOrderTrackLogs = useCallback(async (payload: OrderTrackLogPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.getOrderLogs(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const addOrderNote = useCallback(async (payload: any): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.addOrderNote(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const sendTracer = useCallback(async (payload: SendTracerPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.sendTracer(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);
  const getOrderEvents = useCallback(async (payload: any): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.getOrderEvents(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const createCustomer = useCallback(async (payload: CreateCustomerPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.createCustomer(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const getCreditHistory = useCallback(async (payload: CreditHistory): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.getCreditHistory(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const getCreditNotes = useCallback(async (payload: CreditHistory): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.getCreditNotes(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const getCustomerAddresses = useCallback(async (payload: CustomerAddressPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.getCustomerAddresses(payload);

    return res.isError ? ({} as any) : res.data;
  }, []);

  const getCustomerDetails = useCallback(async (payload: CustomerDetailsPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.account.getCustomerDetails(payload);

    if (!res.isError) {
      if (res?.data?.code === RESPONSE_BAD_REQUEST) {
        ErrorNotification(toast, {
          description: res?.data?.message,
          action: <ErrorIcon className="h-5 w-5" />,
        });
      }
    }

    return res.isError ? ({} as any) : res.data;
  }, []);

  const forgotPassword = useCallback(async (payload: ForgotPassword): Promise<any> => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.account.forgotPassword(payload);
    return res.isError ? ({} as any) : res.data;
  }, []);

  const sendClaimInsuranceEmail = useCallback(async (payload: ClaimInsuranceBody): Promise<any> => {
    const extensions = sdk.composableCommerce;
    const res = await extensions.account.sendClaimInsuranceEmail(payload);
    return res.isError ? ({} as any) : res.data;
  }, []);

  const updateOrder = async (payload: UpdateOrderPayload): Promise<any> => {
    const extensions = sdk?.composableCommerce;
    const res = await extensions.account.updateOrder(payload);

    return res.isError ? ({} as any) : res.data;
  };

  const startPhoneRecording = async (payload: PhoneRecordingPayload): Promise<any> => {
    const extensions = sdk?.composableCommerce;
    const res = await extensions.account.startPhoneRecording(payload);

    return res.isError ? ({} as any) : res.data;
  };

  const stopPhoneRecording = async (payload: PhoneRecordingPayload): Promise<any> => {
    const extensions = sdk?.composableCommerce;
    const res = await extensions.account.stopPhoneRecording(payload);

    return res.isError ? ({} as any) : res.data;
  };

  const validateAddress = async (payload: ValidateAddress): Promise<any> => {
    const extensions = sdk?.composableCommerce;
    const res = await extensions.account.validateAddress(payload);

    return res.isError ? ({} as any) : res.data;
  };

  const getHoldOrders = async (payload: HoldOrder): Promise<any> => {
    const extensions = sdk?.composableCommerce;
    const res = await extensions.account.getHoldOrders(payload);

    return res.isError ? ({} as any) : res.data;
  };

  const decryptCreditCardData = async (creditCardData: string): Promise<Account> => {
    const data = creditCardData;
    const extensions = sdk.composableCommerce;
    const result = await extensions.account.decryptCreditCardData(data);
    return result;
  };

  const tokenizeAndUpdateOrder = async (payload: TokenizeCardPayload): Promise<any> => {
    const extensions = sdk.composableCommerce;

    const res: any = await extensions.account.tokenizeAndUpdateOrder(payload);

    const tokenizeCardResults = res?.data?.TokenizeCardResults ?? {};

    if (tokenizeCardResults && tokenizeCardResults?.bankCode) {
      const bankCode = tokenizeCardResults?.bankCode;
      const paymentErrorMapping = JSON.parse(projectSettings?.paymentErrors ?? '');

      if (Object.keys(paymentErrorMapping).includes(bankCode)) {
        const errorMessage = paymentErrorMapping[bankCode];

        ErrorNotification(toast, {
          description: errorMessage,
          action: <ErrorIcon className="h-5 w-5" />,
        });

        return {
          code: 'FAILED',
          message: 'FAILED',
        };
      }
    }
    return res.isError ? ({} as any) : res.data;
  };

  const isCTAccount = async (payload: CTAccount): Promise<Account> => {
    const extensions = sdk.composableCommerce;
    const result = await extensions.account.isCTAccount(payload);
    return result;
  };

  return {
    requestPasswordReset,
    update,
    getCustomerNotes,
    getCustomerPreferences,
    getOrderHistory,
    getOrderDetails,
    addNewAddress,
    updateCustomerAddress,
    addNote,
    updateCustomerDetails,
    getCustomerRefundCheckHistory,
    getCustomerMailingHistory,
    makePayment,
    updateCustomerPreferences,
    sendCatalog,
    updateEmail,
    updatePhone,
    addDivision,
    getOrderById,
    tokenizeCard,
    enrollCreditProtection,
    getOrderTrackLogs,
    addOrderNote,
    sendTracer,
    getOrderEvents,
    createCustomer,
    getCreditHistory,
    getCreditNotes,
    getCustomerAddresses,
    getCustomerDetails,
    forgotPassword,
    updateOrder,
    startPhoneRecording,
    stopPhoneRecording,
    validateAddress,
    getHoldOrders,
    decryptCreditCardData,
    tokenizeAndUpdateOrder,
    isCTAccount,
    setSelectedSiteInSession,
    sendClaimInsuranceEmail,
  };
};

export default useAccount;
